import Input from '@/components/forms/Input';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { usePostalCodeQuery } from '@/hooks/use-postal-code';
import { Button } from 'ui/button';
import { useFormContext, useWatch } from 'react-hook-form';
import { AffiliateFormValues } from '../affiliate-form.mapper';

export const AddressSection = () => {
  const form = useFormContext<AffiliateFormValues>();
  const { t } = useDynamicLocale();
  const postalCode = useWatch({ control: form.control, name: 'postalCode' }) ?? '';
  const { refetch, isFetching } = usePostalCodeQuery(postalCode.replace(/\D/g, ''));
  const canSearch = /^\d{3}-?\d{4}$/.test(postalCode);

  const autofillAddress = async () => {
    try {
      const { data } = await refetch();
      if (!data) {
        form.setError('postalCode', { type: 'manual', message: 'common.form.validation.invalidPostalCode' });
        return;
      }
      form.clearErrors('postalCode');
      form.setValue('prefecture', data.address1, { shouldValidate: true, shouldDirty: true });
      form.setValue('city', data.address2, { shouldValidate: true, shouldDirty: true });
      form.setValue('street', data.address3, { shouldValidate: true, shouldDirty: true });
    } catch {
      form.setError('postalCode', { type: 'manual', message: 'common.form.validation.invalidPostalCode' });
    }
  };

  return (
    <div className="grid w-full gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
      <div className="lg:pl-12.5 text-primary font-bold">
        <Translation id="affiliate.page.affiliateInfoForm.address" />
      </div>
      <div className="grid grid-cols-2 col-span-2 gap-4">
        <Input
          type="text"
          placeholder="000-0000"
          label="affiliate.page.affiliateInfoForm.postalCode.label"
          forceRequired
          maxLength={8}
          {...form.register('postalCode', {
            onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
              const digits = event.target.value.replace(/\D/g, '').slice(0, 7);
              form.setValue('postalCode', digits.length > 3 ? `${digits.slice(0, 3)}-${digits.slice(3)}` : digits, {
                shouldDirty: true,
              });
            },
          })}
        />
        <div className="flex items-start pt-6">
          <Button
            className="w-fit text-copy rounded-lg"
            type="button"
            onClick={() => void autofillAddress()}
            disabled={!canSearch || isFetching}
            isLoading={isFetching}
          >
            <Translation id="affiliate.page.affiliateInfoForm.autoFill" />
          </Button>
        </div>
        <Input
          {...form.register('prefecture')}
          type="text"
          maxLength={50}
          forceRequired
          label="affiliate.page.affiliateInfoForm.prefecture.label"
          placeholder={t('common.form.placeholder.selectPrefecture')}
        />
        <Input
          className="row-span-4"
          type="text"
          label="affiliate.page.affiliateInfoForm.city.label"
          placeholder={t('common.form.placeholder.enterCity')}
          forceRequired
          {...form.register('city')}
          maxLength={50}
        />
        <Input
          className="row-span-4"
          type="text"
          label="affiliate.page.affiliateInfoForm.street.label"
          placeholder={t('common.form.placeholder.street')}
          forceRequired
          {...form.register('street')}
          maxLength={50}
        />
        <Input
          className="row-span-4"
          type="text"
          label="affiliate.page.affiliateInfoForm.buildingAndRoom.label"
          placeholder={t('common.form.placeholder.buildingAndRoom')}
          {...form.register('buildingNumber')}
          maxLength={50}
        />
      </div>
    </div>
  );
};
